/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { getCluGptService } from 'src/clu/core/services/gpt/cluGptService';
import {
    gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger,
    gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore
} from 'src/clu/modules/handlers/utterances/cluUtteranceMappers';
import { logGptCreate } from 'src/core/services/telemetry/eventsNamesTemplate';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getNextAbleResults } from 'src/core/utils/batchPromises';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { GptJobSwagger } from 'src/customText/core/services/gpt/customTextGptSwagger';
import { gptJobSwaggerToGptJobStore } from 'src/customText/modules/handlers/customTextGptHandlerGroup/gptMappers';
import { getGptJobOperationHandler, isGptFeatureDisabled } from 'src/modules/gptPrediction/utils/gptUtils';

export type CluGptHandlerGroup = ReturnType<typeof createCluGptHandlerGroup>;

export const createCluGptHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;
    const { serviceName } = getPortalFromUrl();

    const generateUtterancesWithGpt = async ({
        intents,
        context,
        utterances,
        azureOpenAiDeploymentName,
        azureOpenAiResourceRegion
    }: HandlerParams<{
        intents: string[];
        utterances: CluUtteranceSwagger[];
        azureOpenAiResourceRegion: string;
        azureOpenAiDeploymentName: string;
    }>) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { name: projectName, gptJobsStoreHouse, id: projectId } = rootStore.currentCluProjectStore;
        const gptService = getCluGptService({ context });

        const resultUrl = await gptService.runGptJob({
            utterances,
            projectName,
            azureOpenAiDeploymentName,
            azureOpenAiResourceRegion,
            intents: intents.map(i => ({ category: i })),
            handler: getGptJobOperationHandler(lifetime, gptJobsStoreHouse)
        });

        logGptCreate(serviceName, projectId);

        await fetchGeneratedUtterancesWithGpt({ resultUrl });
    };

    const fetchCluGptJobs = async ({ context }: HandlerParams<{}>) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { name: projectName, gptJobsStoreHouse } = rootStore.currentCluProjectStore;
        gptJobsStoreHouse.tracker.setState('running');
        const gptJobs = await getNextAbleResults<'jobs', GptJobSwagger>(
            (nextLink: string) => getCluGptService({ context }).getGptJobs({ nextLink, projectName }),
            'jobs'
        );

        gptJobsStoreHouse.setItems(gptJobs.map(gptJobSwaggerToGptJobStore));
        gptJobsStoreHouse.tracker.setState('completed');

        const gptJobsInProgress = gptJobs
            .filter(j => j.status === 'notStarted' || j.status === 'running')
            .sort((j1, j2) => new Date(j2.createdDateTime).getMilliseconds() - new Date(j1.createdDateTime).getMilliseconds());

        if (gptJobsInProgress.length === 0) {
            return;
        }

        gptJobsInProgress.forEach(
            async job =>
                await getCluGptService({ context }).pollGptJob({
                    projectName,
                    jobId: job.jobId,
                    handler: getGptJobOperationHandler(lifetime, gptJobsStoreHouse)
                })
        );
    };

    const fetchGeneratedUtterancesWithGpt = async ({ context, resultUrl }: HandlerParams<{ resultUrl?: string }>) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { name: projectName, intentStoreHouse, gptPredictionsStore, jsonData } = rootStore.currentCluProjectStore;
        gptPredictionsStore.tracker.setState('running');

        const gptResults = await getCluGptService({ context }).getGptResults({ resultUrl, projectName });

        transaction(() => {
            const alreadyDecidedUtterances = [...gptPredictionsStore.acceptedUtterances, ...gptPredictionsStore.rejectedUtterances];
            const invalidUtterances =
                gptResults.utterances?.filter(
                    u =>
                        jsonData.utterances.some(utterance => utterance.text === u.text) ||
                        intentStoreHouse.items.every(intent => intent.name !== u.intent)
                ) ?? [];
            gptPredictionsStore.setRawUtterances(
                gptResults.utterances
                    ?.filter(
                        u =>
                            jsonData.utterances.every(utterance => utterance.text !== u.text) &&
                            alreadyDecidedUtterances.every(utterance => utterance.text !== u.text)
                    )
                    .map(u => ({ ...u, entities: [], dataset: 'Generated' })) ?? []
            );
            gptPredictionsStore.setRejectedUtterances(invalidUtterances.map(gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore));
        });
        gptPredictionsStore.tracker.setState('completed');
    };

    const submitCluGptDecisions = async ({ context }: HandlerParams) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { name: projectName, gptPredictionsStore, projectType } = rootStore.currentCluProjectStore;
        await getCluGptService({ context }).submitGptDecisions({
            projectName,
            projectKind: projectType,
            utterances: gptPredictionsStore.acceptedUtterances
                .map(utterance => gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger(utterance, 'Accepted'))
                .concat(
                    gptPredictionsStore.rejectedUtterances.map(utterance =>
                        gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger(utterance, 'Rejected')
                    )
                )
        });

        transaction(() => {
            gptPredictionsStore.setIsDirty(false);
            gptPredictionsStore.setAcceptedUtterances([]);
            gptPredictionsStore.setRejectedUtterances([]);
        });
    };

    return {
        fetchCluGptJobs,
        submitCluGptDecisions,
        generateUtterancesWithGpt,
        fetchGeneratedUtterancesWithGpt
    };
};
